export const INPUT_PRIORITY = {
  world: 100,
  room: 120,
  inspector: 200,
  pause: 300,
  interaction: 400,
} as const

export type InputHandler = (event: KeyboardEvent) => boolean

interface Registration {
  id: number
  priority: number
  handler: InputHandler
}

/**
 * One capture-phase keyboard router. Higher-priority modal layers receive an
 * event first; returning true prevents lower layers from observing it.
 */
export class ModalInputRouter {
  private readonly registrations = new Map<number, Registration>()
  private nextId = 0
  private listening = false

  register(priority: number, handler: InputHandler): () => void {
    const id = ++this.nextId
    this.registrations.set(id, { id, priority, handler })
    this.syncListener()
    let disposed = false
    return () => {
      if (disposed) return
      disposed = true
      this.registrations.delete(id)
      this.syncListener()
    }
  }

  dispatch(event: KeyboardEvent): boolean {
    const ordered = [...this.registrations.values()].sort(
      (a, b) => b.priority - a.priority || b.id - a.id,
    )
    for (const registration of ordered) {
      if (!registration.handler(event)) continue
      event.stopImmediatePropagation()
      return true
    }
    return false
  }

  get size(): number {
    return this.registrations.size
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    this.dispatch(event)
  }

  private syncListener(): void {
    if (typeof window === 'undefined') return
    if (this.registrations.size > 0 && !this.listening) {
      window.addEventListener('keydown', this.onKeyDown, true)
      this.listening = true
    } else if (this.registrations.size === 0 && this.listening) {
      window.removeEventListener('keydown', this.onKeyDown, true)
      this.listening = false
    }
  }
}

export const modalInputRouter = new ModalInputRouter()
